import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { IconButton } from '@mui/material';
import BTable from '../../../Tools/Table/BTable';
import { AddCircle, Edit } from '@mui/icons-material';
import FP2EDiameterForm from './FP2EDiameterForm';
import useRefresh from '../../../../hooks/useRefresh';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  BirdzDialog,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { useGetCompanies } from '../../../../hooks/datarefs';
import { FP2EItem } from './types';

export default function FP2EDiameters() {
  const { t } = useTranslation();
  const endpoint = '/api/bconnect/webapp/administrator/references/fp2e-diameter/';
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [id, setId] = useState<number | null>(null);
  const { refresh, triggerRefresh } = useRefresh();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { data: clients } = useGetCompanies();
  const { notif, notifOptions } = useNotif();

  const listFields = [
    {
      name: 'Code',
      label: t('referencesTables.code')
    },
    {
      name: 'Value',
      label: t('referencesTables.value')
    },
    {
      name: 'MeterKey',
      label: t('referencesTables.meterKey')
    },
    {
      name: 'companies',
      label: t('referencesTables.clients'),
      transform: (value: number[]) => {
        return value.length ? `${value.length} / ${clients?.length}` : '-';
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  const actions = [
    {
      name: 'edit',
      label: t('referencesTables.diameterActions.actions.edit'),
      render: (item: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.Id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('referencesTables.diameterActions.actions.delete'),
      render: (param: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('referencesTables.diameterActions.actions.delete'),
                content: t('referencesTables.diameterActions.actions.deleteTypeContent'),
                onValidate: () => {
                  setAction('delete');
                  axios
                    .delete(
                      `/api/bconnect/webapp/administrator/references/fp2e-diameter/${param.Id}/`
                    )
                    .then(
                      () => {
                        setAction(null);
                        triggerRefresh();
                        notif({
                          content: t('referencesTables.deleteSuccess'),
                          type: 'success'
                        });
                      },
                      () => {
                        setAction(null);
                        notif({
                          content: t('referencesTables.deleteError'),
                          type: 'success'
                        });
                      }
                    );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  return (
    <>
      <BTable<FP2EItem>
        model={'fp2e-diameter'}
        endpoint={endpoint}
        fields={listFields}
        actions={actions}
        refresh={refresh}
        headerActions={[
          {
            name: t('referencesTables.diameterActions.actions.add'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />

      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('referencesTables.diameterActions.modalMessages.deleteTypeAction')
            : action === 'edit'
              ? t('referencesTables.diameterActions.modalMessages.editTypeAction')
              : t('referencesTables.diameterActions.modalMessages.createTypection')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        showCloseButton={true}
      >
        <FP2EDiameterForm
          endpoint={endpoint}
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
    </>
  );
}
